import type { Active, Over } from "@dnd-kit/core";
import type { BlockCategory } from "@circle-react-shared/BuilderUI/DataModel.types";
import {
  isDroppableData,
  isSerializableBlockExport,
} from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { BlockCategoryName } from "@circle-react-shared/BuilderUI/constants/BlockCategories";

export type DroppableAcceptState = "none" | "accept" | "reject";

export interface AcceptableToDropProps {
  accepts: BlockCategoryName[] | ReadonlyArray<BlockCategoryName>;
  categories: BlockCategory[] | ReadonlyArray<BlockCategory>;
  rejects: BlockCategoryName[] | ReadonlyArray<BlockCategoryName>;
}

export const isAcceptableToDrop = ({
  accepts,
  categories,
  rejects,
}: AcceptableToDropProps) => {
  const hasAccepts = Array.isArray(accepts) && accepts.length > 0;
  const hasRejects = Array.isArray(rejects) && rejects.length > 0;

  if (hasAccepts) {
    return accepts.some(acceptable =>
      categories.some(
        category => category.name.toLowerCase() === acceptable.toLowerCase(),
      ),
    );
  }

  if (hasRejects) {
    return !rejects.some(reject =>
      categories.some(
        category => category.name.toLowerCase() === reject.toLowerCase(),
      ),
    );
  }

  return true;
};

export const droppableAcceptState = (
  droppable?: Over | null,
  draggable?: Active | null,
): DroppableAcceptState => {
  if (!droppable || !draggable) return "none";

  const droppableData = droppable.data.current;
  const draggableData = draggable.data.current;

  if (
    !droppableData ||
    !draggableData ||
    !isSerializableBlockExport(draggableData) ||
    !isDroppableData(droppableData)
  ) {
    return "none";
  }

  const accepts = droppableData.accepts ?? [];
  const rejects = droppableData.rejects ?? [];
  const categories = draggableData.meta.category ?? [];
  const isAcceptable = isAcceptableToDrop({ accepts, categories, rejects });

  return isAcceptable ? "accept" : "reject";
};
